import { useMemo } from "react";

import {
    MIN_SOL_BALANCE,
    NullableRecord,
    SortDirection,
    WRAPPED_SOL_MINT,
    bsMath,
    combineCollections,
    filterNullableRecord,
    lamportsToUiAmount
} from "@bridgesplit/utils";

import { PriceFetchType, StakeSortType, TokenBalanceExpanded, UserVaultPositionStatus } from "../types";
import { useWhirlpoolPositions, whirlpoolPositionToToken } from "./whirlpool";
import { useOraclePrices } from "./pricing";
import { raydiumPositionToToken, useRaydiumPositions } from "./raydium";
import { isRaydiumPosition, isWhirlpoolPosition } from "./clmm";
import { useUserTokens } from "./user-tokens";
import { useLoopscaleWalletContext } from "../wallet";
import { useTokenListContext } from "./token-list";
import { useUserLendingVaultPositions } from "./vaults";
type AssetParams = {
    skip?: boolean;
    fullSolBalance?: boolean;
    includeLpPositions?: boolean;
    solBuffer?: number; //Used as a buffer for solana transactions that to make sure user has enough balance for rent and transaction fees
};

// Get the available assets for user to deposit
export function useUserAvailableAssets({ skip, fullSolBalance, includeLpPositions }: AssetParams) {
    const {
        data: { userWalletAddress: activeWallet },
        isLoading: isWalletLoading
    } = useLoopscaleWalletContext();

    const walletAssets = useWalletAvailableAssets(activeWallet, {
        skip: !activeWallet || isWalletLoading || skip,
        fullSolBalance,
        includeLpPositions
    });

    const data = useMemo(() => {
        if (!activeWallet || isWalletLoading) return [];
        return walletAssets;
    }, [activeWallet, isWalletLoading, walletAssets]);

    // keeps parity with before but would like to reverify is better way to check loading state
    const isLoading = isWalletLoading || (!!activeWallet && !walletAssets);
    return { data, isLoading };
}

export function useWalletAvailableAssets(wallet: string | undefined, params: AssetParams = {}) {
    const { skip, fullSolBalance, includeLpPositions: includeLpPositionsParam, solBuffer } = params;
    const includeLpPositions = includeLpPositionsParam ?? false;

    const { tokens, isLoading: tokensLoading, walletAddress } = useUserTokens();
    const { getMetadata } = useTokenListContext();

    const shouldSkip = !!skip || !wallet || walletAddress !== wallet;
    const tokenAccounts = shouldSkip ? undefined : tokens;

    const possibleWpNfts = useMemo(
        () =>
            tokenAccounts
                ?.filter((t) => t.tokenAmount.uiAmount === 1 && t.tokenAmount.decimals === 0)
                .map((m) => m.mint),
        [tokenAccounts]
    );

    const { data: wp, isLoading: wpLoading } = useWhirlpoolPositions(possibleWpNfts, {
        skip: shouldSkip || !includeLpPositions
    });

    const { data: raydiumPositions, isLoading: raydiumPositionsLoading } = useRaydiumPositions(possibleWpNfts, {
        skip: shouldSkip || !includeLpPositions
    });

    const clmmPositionRows = useMemo(
        () => combineCollections([wp?.map(whirlpoolPositionToToken), raydiumPositions?.map(raydiumPositionToToken)]),
        [wp, raydiumPositions]
    );

    const { data: userPositions, isLoading: vaultPositionsLoading } = useUserLendingVaultPositions(
        {
            userAddress: walletAddress ?? "",
            status: UserVaultPositionStatus.Active,
            sort: StakeSortType.StartTime,
            sortDirection: SortDirection.Desc,
            page: 0,
            pageSize: 100
        },
        shouldSkip
    );

    const lpMintToLpBalance = useMemo(() => {
        if (!userPositions?.length) return undefined;
        return userPositions.reduce((acc, position) => {
            const lp = position.vaultLpBalance;
            acc[position.vaultExpanded.vault.lpMint] = (acc[position.vaultExpanded.vault.lpMint] ?? 0) + lp;
            return acc;
        }, {} as Record<string, number>);
    }, [userPositions]);

    return useMemo(() => {
        if (shouldSkip) return undefined;

        if (
            tokensLoading ||
            (wpLoading && includeLpPositions) ||
            (raydiumPositionsLoading && includeLpPositions) ||
            vaultPositionsLoading
        ) {
            return undefined;
        }

        if (tokenAccounts === undefined) return undefined;

        const totalSolReserve = (solBuffer ?? 0) + MIN_SOL_BALANCE;

        // Vault LP tokens should not currently ever be in the wallet, but in the case they are, ignore them
        // removed filter because safety check added queries and we should never be in that state
        // in the case that is wrong, add back a filter to query lend vaults and check for lp tokens
        const baseTokenRows = tokenAccounts.map(
            ({
                mint,
                tokenAmount: { uiAmount: uiAmountRaw, amount, decimals }
            }): NullableRecord<TokenBalanceExpanded> => {
                const metadata = getMetadata(mint);
                const uiAmount = uiAmountRaw !== null ? uiAmountRaw : lamportsToUiAmount(parseInt(amount), decimals);
                const uiAmountAdjusted =
                    !fullSolBalance && mint === WRAPPED_SOL_MINT
                        ? Math.max(0, bsMath.tokenSub(uiAmount, totalSolReserve, decimals))
                        : uiAmount;

                return { key: mint, metadata, amount: uiAmountAdjusted };
            }
        );

        const vaultLpRows: NullableRecord<TokenBalanceExpanded>[] = Object.entries(lpMintToLpBalance ?? {})
            .filter(([, maxWithdrawLp]) => !!maxWithdrawLp)
            .map(([lpMint, maxWithdrawLp]) => ({
                key: lpMint,
                metadata: getMetadata(lpMint),
                amount: maxWithdrawLp
            }));

        return baseTokenRows
            .concat(vaultLpRows)
            .concat(clmmPositionRows)
            .filter(filterNullableRecord)
            .filter(({ amount }) => !!amount);
    }, [
        shouldSkip,
        tokensLoading,
        wpLoading,
        raydiumPositionsLoading,
        includeLpPositions,
        vaultPositionsLoading,
        tokenAccounts,
        getMetadata,
        fullSolBalance,
        solBuffer,
        clmmPositionRows,
        lpMintToLpBalance
    ]);
}

export function useWalletBalanceByMint(wallet: string | undefined, mint: string | undefined, params: AssetParams = {}) {
    const assets = useWalletAvailableAssets(wallet, params);
    const data = assets?.find((a) => a.metadata.mint === mint);

    return { data, isLoading: !assets };
}

export function useUserUnrecognizedAssets(skip?: boolean) {
    const {
        data: { userWalletAddress: activeWallet },
        isLoading: isWalletLoading
    } = useLoopscaleWalletContext();

    const { tokens, isLoading: tokensLoading } = useUserTokens();
    const { getMetadata, isLoading: isMetadataLoading } = useTokenListContext();

    const shouldSkip = skip || !activeWallet || isWalletLoading;

    const data = shouldSkip ? undefined : tokens?.filter((t) => !getMetadata(t.mint));

    const isLoading = shouldSkip ? false : tokensLoading || isMetadataLoading;

    return { data, isLoading };
}

//Balance map is a map of allowed tokens to their amount and usd value. For CLMMs, this is the POOL to the amount of postiions.
export function useSummarizedUserAssets(params: AssetParams) {
    const { data: balances, isLoading } = useUserAvailableAssets(params);
    const { getUsdPrice } = useOraclePrices(
        balances?.map((b) => b.key),
        PriceFetchType.Twap
    );

    const balanceMap = useMemo(() => {
        if (!balances) return undefined;

        const balanceMap = balances.reduce((map, curr) => {
            const key = getAssetKey(curr);

            const prev = map.get(key) ?? { available: 0, availableUsd: 0 };
            const currentUsdValue = (getUsdPrice(key) ?? 0) * curr.amount;

            if (curr.clmmPosition) {
                map.set(key, {
                    available: prev.available + 1,
                    availableUsd: Math.max(prev.availableUsd, curr.clmmPosition.totalPrice ?? 0)
                });
            } else {
                map.set(key, {
                    available: prev.available + curr.amount,
                    availableUsd: prev.availableUsd + currentUsdValue
                });
            }
            return map;
        }, new Map<string, { available: number; availableUsd: number }>());

        return balanceMap;
    }, [balances, getUsdPrice]);

    return { balanceMap, balances, isLoading };
}

function getAssetKey(asset: TokenBalanceExpanded) {
    if (asset.clmmPosition) {
        if (isWhirlpoolPosition(asset.clmmPosition)) {
            return asset.clmmPosition.whirlpoolAddress;
        } else if (isRaydiumPosition(asset.clmmPosition)) {
            return asset.clmmPosition.poolAddress;
        }
    }
    return asset.metadata.mint;
}
